import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    HostListener, input, model, effect, signal,
    viewChild
} from '@angular/core';
import {DynamicComponentBase} from '../../service/ng-dynamic-components.service';
import {Stdlib} from '../../lib/stdlib';
import {FormsModule} from '@angular/forms';
import {AppComponent} from '../../app.component';

const defaultOptions = {
    fontSize: 10,
    automaticLayout: true,
    minimap: {
        enabled: false
    },
};

interface MonacoIframeApi {
    setValue(value: string): void;
    getValue(): string;
    setLanguage(language: string): void;
    setFontSize(fontSize: number): void;
    setLineNumbers(show: boolean): void;
    setTheme(theme: string): void;
    setWordWrap(wordWrap: string): void;
    setMinimap(enabled: boolean): void;
}


@Component({
    selector: 'app-monaco-editor',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        FormsModule
    ],
    templateUrl: './monaco-editor.component.html',
    styleUrl: './monaco-editor.component.scss'
})
export class MonacoEditorComponent extends DynamicComponentBase {

    protected ready = signal(false);

    public readonly showToolbar = model<boolean>(true);
    public readonly initialValue = input<string | null>(null);
    public readonly language = model<string>('xml');
    public readonly lineNos = model<boolean>(true);
    public readonly wordWrap = model<boolean>(false);
    public readonly darkMode = model<boolean>(false);
    public readonly minimap = model<boolean>(false);
    public readonly fontSize = model<number>(11);

    private editorIframe = viewChild<ElementRef<HTMLIFrameElement>>('editor');
    private editorApi: MonacoIframeApi | undefined;

    constructor() {
        super();
        effect(() => this.setLanguage(this.language()));
        effect(() => this.setLineNumbers(this.lineNos()));
        effect(() => this.setDark(this.darkMode()));
        effect(() => this.setWordWrap(this.wordWrap()));
        effect(() => this.setMinimap(this.minimap()));
        effect(() => this.setFontSize(this.fontSize()));
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent) {
        if (event.source === this.editorIframe()?.nativeElement?.contentWindow && event.data?.type === 'monaco-ready') {
            console.log('Monaco editor is ready');
            this.ready.set(true);
            const win = this.editorIframe()!.nativeElement.contentWindow as any
            this.editorApi = win.api as MonacoIframeApi;
            if (this.initialValue()) {
                this.setValue(this.initialValue()!);
                Stdlib.let(this.language(), lang => this.setLanguage(lang));
                Stdlib.let(this.lineNos(), lineNos => this.setLineNumbers(lineNos));
                Stdlib.let(this.darkMode(), dark => this.setDark(dark));
            }
        }
    }

    setValue(value: string) {
        this.editorApi?.setValue(value);
    }

    getValue(): string {
        return this.editorApi?.getValue() ?? '';
    }

    setLanguage(language: string) {
        this.editorApi?.setLanguage(language);
    }

    setFontSize(fontSize: number) {
        this.editorApi?.setFontSize(fontSize);
    }

    setLineNumbers(show: boolean) {
        this.editorApi?.setLineNumbers(show);
    }

    setDark(dark: boolean) {
        this.setTheme(dark ? 'vs-dark' : 'vs');
    }

    setTheme(theme: string) {
        this.editorApi?.setTheme(theme);
    }

    setWordWrap(wordWrap: boolean) {
        this.editorApi?.setWordWrap(wordWrap ? 'on' : 'off');
    }

    setMinimap(enabled: boolean) {
        this.editorApi?.setMinimap(enabled);
    }

    protected async onCopy() {
        await navigator.clipboard.writeText(this.getValue());
        AppComponent.messageToast.show({ message: 'Copied to clipboard' });
    }
}
